import { useEffect, useState } from 'react';
import { api, Attempt, AttemptHistoryRow } from '../api';
import { Icon } from '../Icon';

/**
 * The tab that last held a live test. Per tab, not per browser: a reload puts
 * the learner straight back in, while a fresh tab or a new sign-in gets the
 * banner below and chooses for themselves.
 */
const ACTIVE_KEY = 'hazelled.activeAttempt';

export function rememberActiveAttempt(id: string | null) {
  try {
    if (id) sessionStorage.setItem(ACTIVE_KEY, id);
    else sessionStorage.removeItem(ACTIVE_KEY);
  } catch { /* best effort: the banner still offers the way back */ }
}

export function rememberedActiveAttempt(): string | null {
  try { return sessionStorage.getItem(ACTIVE_KEY); } catch { return null; }
}

/**
 * Reload an attempt from the server. `finished` means it can no longer be
 * answered (or is not this learner's); `unreachable` means we could not ask,
 * and says nothing about the attempt — it may well still be running.
 */
export type Reopened =
  | { kind: 'open'; attempt: Attempt }
  | { kind: 'finished' }
  | { kind: 'unreachable' };

export async function reopenAttempt(id: string): Promise<Reopened> {
  try {
    const attempt = await api.get<Attempt>(`/attempts/${id}`);
    return attempt.state === 'in_progress' ? { kind: 'open', attempt } : { kind: 'finished' };
  } catch (e: any) {
    return e?.status >= 400 && e?.status < 500 ? { kind: 'finished' } : { kind: 'unreachable' };
  }
}

/**
 * Seconds left on whatever clock governs the attempt right now, or null when
 * nothing is counting (study mode, or between sections). Read from the
 * server's figures — the clock kept running while the learner was away.
 */
function secondsLeft(a: Attempt): number | null {
  if (!a.sections) return a.remainingSeconds;
  return a.sections.find((s) => s.state === 'open')?.remainingSeconds ?? null;
}

function timeOut(a: Attempt) {
  if (secondsLeft(a) !== 0) return false;
  // A section running out with another still to come is a break, not the end.
  return !a.sections?.some((s) => s.state === 'not_started');
}

function describeTime(a: Attempt) {
  const left = secondsLeft(a);
  if (timeOut(a)) return 'Time ran out while you were away. Your answers so far will be scored.';
  if (left === 0) return 'This section’s time ran out while you were away. The next section is ready when you are.';
  if (left !== null) {
    const min = Math.ceil(left / 60);
    return `About ${min} minute${min === 1 ? '' : 's'} left — the clock kept running while you were away.`;
  }
  if (a.sections) return 'You are between sections. The next one starts when you choose.';
  return 'No time limit.';
}

/**
 * Attempts the learner started and left without finishing — a closed tab, a
 * dropped connection, a phone that went to sleep. Each can be picked up where
 * it stands on the server: same questions, same answers, same clock.
 */
export function ResumeBanner({ onResume }: { onResume: (attempt: Attempt) => void }) {
  const [open, setOpen] = useState<{ row: AttemptHistoryRow; attempt: Attempt }[]>([]);

  useEffect(() => {
    let live = true;
    api.get<AttemptHistoryRow[]>('/me/attempts')
      .then(async (rows) => {
        const found = await Promise.all(rows
          .filter((r) => r.state === 'in_progress')
          .map(async (row) => {
            const found = await reopenAttempt(row.attemptId);
            return found.kind === 'open' ? { row, attempt: found.attempt } : null;
          }));
        if (live) setOpen(found.filter((f): f is NonNullable<typeof f> => f !== null));
      })
      .catch(() => { /* the banner is a convenience; the catalogue still works */ });
    return () => { live = false; };
  }, []);

  if (!open.length) return null;

  return (
    <section className="notice" aria-labelledby="resume-heading">
      <h2 id="resume-heading" style={{ marginTop: 0 }}>
        {open.length === 1 ? 'You have a test in progress' : 'You have tests in progress'}
      </h2>
      {open.map(({ row, attempt }) => {
        const answered = attempt.responses.filter((r) => r.selectedResponse?.length).length;
        const finished = timeOut(attempt);
        return (
          <div key={attempt.id} style={{ marginTop: '0.75rem' }}>
            <p style={{ margin: 0 }}>
              <strong>{row.subject}</strong> <span className="muted">{row.edition}</span>
              {' · '}{attempt.mode === 'study' ? 'Study' : 'Timed'}
              {' · '}{answered} of {attempt.questionOrder.length} answered
            </p>
            <p className="muted" style={{ margin: '0.25rem 0 0.5rem' }}>{describeTime(attempt)}</p>
            <button className="btn-primary" onClick={() => onResume(attempt)}>
              <Icon name="play" />{finished ? 'Finish and see result' : 'Resume test'}
              <span className="visually-hidden"> in {row.subject}</span>
            </button>
          </div>
        );
      })}
    </section>
  );
}
